import React, { CSSProperties, useState } from 'react';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockIcon from '@mui/icons-material/Lock';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { currencyFormatter } from 'utils/functions';

type CreditCardFormProps = {
  amount: number;
  onSubmit: (data: {
    cardNumber: string;
    expirationDate: string;
    cardCode: string;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  }) => void;
  onBack: () => void;
};

/* ── Card utilities ── */

const detectCardType = (number: string): string => {
  const d = number.replace(/\D/g, '');
  if (/^4/.test(d)) return 'visa';
  if (/^5[1-5]/.test(d)) return 'mastercard';
  if (/^3[47]/.test(d)) return 'amex';
  if (/^6(?:011|5)/.test(d)) return 'discover';
  return 'unknown';
};

const luhnCheck = (num: string): boolean => {
  let sum = 0;
  let shouldDouble = false;
  for (let i = num.length - 1; i >= 0; i--) {
    let digit = parseInt(num.charAt(i));
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
};

const isExpiryValid = (formatted: string): boolean => {
  // formatted = "YYYY-MM"
  if (!formatted || formatted.length < 6) return false;
  const [yearStr, monthStr] = formatted.split('-');
  const year = parseInt(yearStr);
  const month = parseInt(monthStr);
  if (month < 1 || month > 12) return false;
  const now = new Date();
  const expiry = new Date(year, month); // first of next month
  return expiry > now;
};

const CARD_COLORS: Record<string, string> = {
  visa: '#1a1f71',
  mastercard: '#eb001b',
  discover: '#ff6000',
  amex: '#006fcf',
  unknown: 'rgba(255,255,255,0.1)'
};

const CARD_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  discover: 'Discover',
  amex: 'Amex',
  unknown: ''
};

const CreditCardForm = ({ amount, onSubmit, onBack }: CreditCardFormProps) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('United States');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const rawCard = cardNumber.replace(/\s/g, '');
  const cardType = detectCardType(rawCard);
  const isAmex = cardType === 'amex';
  const maxCvv = isAmex ? 4 : 3;

  // Format card number with spaces
  const handleCardNumber = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 16);
    const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
    setCardNumber(formatted);
  };

  // Format expiry as MM / YY
  const handleExpiry = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) {
      setExpiry(`${digits.slice(0, 2)} / ${digits.slice(2)}`);
    } else {
      setExpiry(digits);
    }
  };

  // Transform MM / YY to YYYY-MM for API
  const getFormattedExpiry = (): string => {
    const digits = expiry.replace(/\D/g, '');
    if (digits.length < 4) return '';
    const mm = digits.slice(0, 2);
    const yy = digits.slice(2, 4);
    return `20${yy}-${mm}`;
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    // Card number
    if (rawCard.length < 13) {
      errs.cardNumber = 'Enter a valid card number';
    } else if (!luhnCheck(rawCard)) {
      errs.cardNumber = 'Invalid card number';
    } else if (isAmex) {
      errs.cardNumber = 'American Express cards are not accepted';
    }

    // Expiry
    const formattedExpiry = getFormattedExpiry();
    if (!formattedExpiry) {
      errs.expiry = 'Enter expiry (MM/YY)';
    } else if (!isExpiryValid(formattedExpiry)) {
      errs.expiry = 'Card has expired';
    }

    // CVV
    if (cvv.length < 3) errs.cvv = 'Enter CVV';

    // Billing
    if (!firstName.trim()) errs.firstName = 'Required';
    if (!lastName.trim()) errs.lastName = 'Required';
    if (!address.trim()) errs.address = 'Required';
    if (!city.trim()) errs.city = 'Required';
    if (!state.trim()) errs.state = 'Required';
    if (!zip.trim()) errs.zip = 'Required';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    onSubmit({
      cardNumber: rawCard,
      expirationDate: getFormattedExpiry(),
      cardCode: cvv,
      firstName,
      lastName,
      address,
      city,
      state,
      zip,
      country
    });
  };

  return (
    <div style={containerStyle}>
      <button onClick={onBack} style={backBtn}>← Back</button>

      <div style={headerRow}>
        <CreditCardIcon style={{ fontSize: '1.2rem', color: '#E98C26' }} />
        <span style={headerText}>Card Details</span>
        <div style={{ flex: 1 }} />
        <LockIcon style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)' }} />
        <span style={secureText}>Secure</span>
      </div>

      {/* Card number with type indicator */}
      <div style={fieldGroup}>
        <label style={labelStyle}>Card Number</label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="1234 5678 9012 3456"
            value={cardNumber}
            onChange={(e) => handleCardNumber(e.target.value)}
            style={{
              ...inputStyle,
              ...(errors.cardNumber ? inputErrorStyle : {}),
              paddingRight: '70px'
            }}
            autoComplete="cc-number"
          />
          {rawCard.length >= 2 && cardType !== 'unknown' && (
            <div style={cardBadge}>
              <span style={{
                ...cardBadgeText,
                backgroundColor: CARD_COLORS[cardType],
                color: cardType === 'discover' ? '#000' : '#fff'
              }}>
                {CARD_LABELS[cardType]}
              </span>
            </div>
          )}
        </div>
        {errors.cardNumber && <FieldError message={errors.cardNumber} />}
        {isAmex && !errors.cardNumber && (
          <span style={warningStyle}>
            <ErrorOutlineIcon style={{ fontSize: '0.7rem' }} /> Amex not accepted
          </span>
        )}
      </div>

      {/* Expiry + CVV row */}
      <div style={rowStyle}>
        <div style={{ ...fieldGroup, flex: 1 }}>
          <label style={labelStyle}>Expiry</label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="MM / YY"
            value={expiry}
            onChange={(e) => handleExpiry(e.target.value)}
            style={{ ...inputStyle, ...(errors.expiry ? inputErrorStyle : {}) }}
            autoComplete="cc-exp"
          />
          {errors.expiry && <FieldError message={errors.expiry} />}
        </div>
        <div style={{ ...fieldGroup, flex: 1 }}>
          <label style={labelStyle}>CVV</label>
          <input
            type="text"
            inputMode="numeric"
            placeholder={isAmex ? '1234' : '123'}
            maxLength={maxCvv}
            value={cvv}
            onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, maxCvv))}
            style={{ ...inputStyle, ...(errors.cvv ? inputErrorStyle : {}) }}
            autoComplete="cc-csc"
          />
          {errors.cvv && <FieldError message={errors.cvv} />}
        </div>
      </div>

      <div style={divider} />

      {/* Name row */}
      <div style={rowStyle}>
        <div style={{ ...fieldGroup, flex: 1 }}>
          <label style={labelStyle}>First Name</label>
          <input
            type="text"
            placeholder="John"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={{ ...inputStyle, ...(errors.firstName ? inputErrorStyle : {}) }}
            autoComplete="cc-given-name"
          />
          {errors.firstName && <FieldError message={errors.firstName} />}
        </div>
        <div style={{ ...fieldGroup, flex: 1 }}>
          <label style={labelStyle}>Last Name</label>
          <input
            type="text"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            style={{ ...inputStyle, ...(errors.lastName ? inputErrorStyle : {}) }}
            autoComplete="cc-family-name"
          />
          {errors.lastName && <FieldError message={errors.lastName} />}
        </div>
      </div>

      {/* Address */}
      <div style={fieldGroup}>
        <label style={labelStyle}>Street Address</label>
        <input
          type="text"
          placeholder="123 Main St"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          style={{ ...inputStyle, ...(errors.address ? inputErrorStyle : {}) }}
          autoComplete="street-address"
        />
        {errors.address && <FieldError message={errors.address} />}
      </div>

      {/* City + State */}
      <div style={rowStyle}>
        <div style={{ ...fieldGroup, flex: 2 }}>
          <label style={labelStyle}>City</label>
          <input
            type="text"
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={{ ...inputStyle, ...(errors.city ? inputErrorStyle : {}) }}
            autoComplete="address-level2"
          />
          {errors.city && <FieldError message={errors.city} />}
        </div>
        <div style={{ ...fieldGroup, flex: 1 }}>
          <label style={labelStyle}>State</label>
          <input
            type="text"
            placeholder="TX"
            maxLength={2}
            value={state}
            onChange={(e) => setState(e.target.value.toUpperCase())}
            style={{ ...inputStyle, ...(errors.state ? inputErrorStyle : {}) }}
            autoComplete="address-level1"
          />
          {errors.state && <FieldError message={errors.state} />}
        </div>
      </div>

      {/* Zip + Country */}
      <div style={rowStyle}>
        <div style={{ ...fieldGroup, flex: 1 }}>
          <label style={labelStyle}>ZIP Code</label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="12345"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            style={{ ...inputStyle, ...(errors.zip ? inputErrorStyle : {}) }}
            autoComplete="postal-code"
          />
          {errors.zip && <FieldError message={errors.zip} />}
        </div>
        <div style={{ ...fieldGroup, flex: 1 }}>
          <label style={labelStyle}>Country</label>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={selectStyle}
          >
            <option value="United States">United States</option>
            <option value="Canada">Canada</option>
            <option value="United Kingdom">United Kingdom</option>
            <option value="Australia">Australia</option>
            <option value="Germany">Germany</option>
            <option value="France">France</option>
            <option value="Brazil">Brazil</option>
            <option value="Philippines">Philippines</option>
            <option value="Japan">Japan</option>
            <option value="India">India</option>
          </select>
        </div>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={isAmex}
        style={{ ...submitBtn, ...(isAmex ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}
      >
        Pay {currencyFormatter(amount)}
      </button>

      <span style={disclaimerText}>
        Please use the same email and phone number associated with your Hijack account to ensure funds are directed correctly.
      </span>

      <span style={disclaimerText}>
        Your card will be charged immediately. All transactions are encrypted and secure.
      </span>
    </div>
  );
};

const FieldError = ({ message }: { message: string }) => (
  <span style={errorStyle}>{message}</span>
);

export default CreditCardForm;

/* ── Styles ── */

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px'
};

const backBtn: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'rgba(255,255,255,0.5)',
  fontSize: '0.75rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  padding: 0,
  alignSelf: 'flex-start'
};

const headerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '4px'
};

const headerText: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 700,
  color: '#fff'
};

const secureText: CSSProperties = {
  fontSize: '0.6rem',
  color: 'rgba(255,255,255,0.3)',
  fontWeight: 600
};

const fieldGroup: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px'
};

const labelStyle: CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.4)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const inputStyle: CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '0.85rem',
  fontWeight: 600,
  padding: '10px 12px',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box'
};

const inputErrorStyle: CSSProperties = {
  borderColor: 'rgba(231, 76, 60, 0.5)',
  backgroundColor: 'rgba(231, 76, 60, 0.05)'
};

const rowStyle: CSSProperties = {
  display: 'flex',
  gap: '8px'
};

const divider: CSSProperties = {
  height: 1,
  backgroundColor: 'rgba(255,255,255,0.06)',
  margin: '4px 0'
};

const errorStyle: CSSProperties = {
  fontSize: '0.6rem',
  color: '#e74c3c',
  fontWeight: 600
};

const warningStyle: CSSProperties = {
  fontSize: '0.6rem',
  color: '#E98C26',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '4px'
};

const cardBadge: CSSProperties = {
  position: 'absolute',
  right: '10px',
  top: '50%',
  transform: 'translateY(-50%)'
};

const cardBadgeText: CSSProperties = {
  fontSize: '0.55rem',
  fontWeight: 800,
  padding: '3px 8px',
  borderRadius: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.06em'
};

const submitBtn: CSSProperties = {
  width: '100%',
  padding: '14px',
  backgroundColor: '#E98C26',
  border: 'none',
  borderRadius: '12px',
  color: '#fff',
  fontSize: '0.95rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginTop: '8px'
};

const selectStyle: CSSProperties = {
  ...inputStyle,
  backgroundColor: '#1a2332',
  appearance: 'auto' as any
};

const disclaimerText: CSSProperties = {
  fontSize: '0.6rem',
  color: 'rgba(255,255,255,0.25)',
  textAlign: 'center',
  lineHeight: 1.4
};
